import { create } from "zustand";

import type { NotificationOut } from "@/api/schemas";

interface NotificationsStore {
  unreadCount: number;
  recent: NotificationOut[];
  hydrate: (items: NotificationOut[], unreadCount: number) => void;
  prepend: (item: NotificationOut) => void;
  markRead: (notificationId: string) => void;
  markAllRead: () => void;
}

const RECENT_CAP = 50;

/**
 * Live store for the bell badge and dropdown.
 *
 * Hydrated from the initial GET /api/notifications fetch (mounted in
 * NotificationsBootstrap), then patched live by `notification` WS frames.
 * Marking read on the API is fire-and-forget; the store is updated optimistically
 * so the badge counter feels instant.
 */
export const useNotifications = create<NotificationsStore>((set) => ({
  unreadCount: 0,
  recent: [],

  hydrate: (items, unreadCount) =>
    set({ recent: items.slice(0, RECENT_CAP), unreadCount }),

  prepend: (item) =>
    set((state) => {
      const dedup = state.recent.filter((n) => n.id !== item.id);
      return {
        recent: [item, ...dedup].slice(0, RECENT_CAP),
        unreadCount: state.unreadCount + (item.read_at ? 0 : 1),
      };
    }),

  markRead: (notificationId) =>
    set((state) => {
      let decremented = 0;
      const recent = state.recent.map((n) => {
        if (n.id !== notificationId) return n;
        if (!n.read_at) {
          decremented += 1;
          return { ...n, read_at: new Date().toISOString() };
        }
        return n;
      });
      return {
        recent,
        unreadCount: Math.max(0, state.unreadCount - decremented),
      };
    }),

  markAllRead: () =>
    set((state) => ({
      recent: state.recent.map((n) =>
        n.read_at ? n : { ...n, read_at: new Date().toISOString() },
      ),
      unreadCount: 0,
    })),
}));
